import React, { useMemo, useCallback, useState } from 'react';
import { Table, Input, Typography, Button, Tooltip } from 'antd';
import { EyeInvisibleOutlined, CopyOutlined } from '@ant-design/icons';
import { useTheme } from '../../../contexts/ThemeContext';

const { Text } = Typography;

function sanitizeTree(nodes) {
  const list = Array.isArray(nodes) ? nodes : [];
  return list.map(n => {
    const children = sanitizeTree(n.children || []);
    const hasChildren = children.length > 0;
    return {
      ...n,
      children: hasChildren ? children : undefined
    };
  });
}

function round2(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return undefined;
  // EPSILON evita os casos tipo 1.005 arredondar para 1.00
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

const BudgetTable = ({
  categories,
  showVM,
  showVP,
  showVO,
  getAggregatedValue,
  isLeaf,
  onChangeValue,
  monthNames,
  hiddenMonths = new Set(),
  onToggleMonth,
  showZeroRows = true
}) => {
  const { theme } = useTheme();
  const dataSource = useMemo(() => sanitizeTree(categories), [categories]);
  const [editingValues, setEditingValues] = useState({});

  // Função para verificar se uma linha deve ser exibida
  // Regra: Ocultar se VM de todos os meses = 0 E (VO e VP são nulos ou zerados)
  // Mas apenas se showZeroRows for false
  const shouldShowRow = useMemo(() => {
    // Se showZeroRows é true, mostrar todas as linhas
    if (showZeroRows) return () => true;
    // Função auxiliar para obter valores
    const getValueFor = (record, month, field) => {
      if (!record) return undefined;
      if (record.__virtual) {
        const children = record.children || [];
        let sum = 0;
        let hasAny = false;
        children.forEach((child) => {
          const childVal = getValueFor(child, month, field);
          if (typeof childVal === 'number') {
            sum += childVal;
            hasAny = true;
          }
        });
        return hasAny ? round2(sum) : undefined;
      }
      const raw = getAggregatedValue(record.id, month, field);
      return typeof raw === 'number' ? round2(raw) : raw;
    };

    return (record) => {
      // Verifica se VM de todos os meses = 0
      let allVMZero = true;
      let hasVMValue = false;

      for (let month = 1; month <= 12; month++) {
        if (hiddenMonths && hiddenMonths.has(month)) continue;
        
        const vmVal = getValueFor(record, month, 'vm');
        if (vmVal !== null && vmVal !== undefined) {
          hasVMValue = true;
          if (typeof vmVal === 'number' && vmVal !== 0) {
            allVMZero = false;
          }
        } else {
          // Se VM é nulo, não pode ocultar (ainda não foi preenchido)
          allVMZero = false;
        }
      }

      // Se não tem valores de VM ou se algum VM não é zero, mostrar
      if (!hasVMValue || !allVMZero) return true;

      // Se chegou aqui, todos os VMs são zero
      // Agora verifica se VO e VP são nulos ou zerados
      let allVONullOrZero = true;
      let allVPNullOrZero = true;

      for (let month = 1; month <= 12; month++) {
        if (hiddenMonths && hiddenMonths.has(month)) continue;

        const voVal = getValueFor(record, month, 'vo');
        const vpVal = getValueFor(record, month, 'vp');

        // Verifica VO
        if (voVal !== null && voVal !== undefined) {
          if (typeof voVal === 'number' && voVal !== 0) {
            allVONullOrZero = false;
          }
        }

        // Verifica VP
        if (vpVal !== null && vpVal !== undefined) {
          if (typeof vpVal === 'number' && vpVal !== 0) {
            allVPNullOrZero = false;
          }
        }
      }

      // Se VM todos = 0 E VO e VP são nulos ou zerados, ocultar
      if (allVMZero && allVONullOrZero && allVPNullOrZero) {
        return false;
      }

      // Caso contrário, mostrar
      return true;
    };
  }, [getAggregatedValue, hiddenMonths, showZeroRows]);

  const filteredDataSource = useMemo(() => {
    const filterTree = (nodes) => {
      if (!Array.isArray(nodes)) return [];
      return nodes
        .map((node) => {
          const children = filterTree(node.children || []);
          const shouldShow = shouldShowRow(node);
          
          if (shouldShow || children.length > 0) {
            return {
              ...node,
              children: children.length > 0 ? children : undefined,
            };
          }
          return null;
        })
        .filter(Boolean);
    };

    return filterTree(dataSource);
  }, [dataSource, shouldShowRow]);

  const monthLabels = useMemo(() => {
    if (Array.isArray(monthNames) && monthNames.length === 12) {
      return monthNames;
    }
    return [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
  }, [monthNames]);

  const makeCellKey = useCallback((recordId, month, scenario) => (
    `${recordId}-${month}-${scenario}`
  ), []);

  const parseBudgetInput = useCallback((rawValue) => {
    if (rawValue === null || rawValue === undefined) return undefined;
    const text = String(rawValue).trim();
    if (!text) return undefined;

    const compact = text.replace(/\s/g, '');
    const normalized = compact.includes(',')
      ? compact.replace(/\./g, '').replace(',', '.')
      : compact;
    const parsed = Number(normalized);
    if (Number.isNaN(parsed)) return undefined;
    return round2(parsed);
  }, []);

  const formatEditableValue = useCallback((value) => {
    if (value === null || value === undefined) return '';
    return String(value).replace('.', ',');
  }, []);

  const setCellEditingValue = useCallback((recordId, month, scenario, value) => {
    const cellKey = makeCellKey(recordId, month, scenario);
    // Debug temporário para rastrear perda de vírgula/zero durante digitação.
    console.debug('[BudgetTable] input change', { cellKey, value });
    setEditingValues((prev) => ({
      ...prev,
      [cellKey]: value,
    }));
  }, [makeCellKey]);

  const commitCellValue = useCallback((recordId, month, scenario) => {
    const cellKey = makeCellKey(recordId, month, scenario);
    if (!Object.prototype.hasOwnProperty.call(editingValues, cellKey)) return;

    const parsedValue = parseBudgetInput(editingValues[cellKey]);
    // Debug temporário para validar valor bruto e valor persistido.
    console.debug('[BudgetTable] input commit', {
      cellKey,
      raw: editingValues[cellKey],
      parsedValue,
    });
    onChangeValue(recordId, month, scenario, parsedValue);

    setEditingValues((prev) => {
      const next = { ...prev };
      delete next[cellKey];
      return next;
    });
  }, [editingValues, makeCellKey, onChangeValue, parseBudgetInput]);

  const getCellInputValue = useCallback((recordId, month, scenario, fallbackNumberValue) => {
    const cellKey = makeCellKey(recordId, month, scenario);
    if (Object.prototype.hasOwnProperty.call(editingValues, cellKey)) {
      return editingValues[cellKey];
    }
    return typeof fallbackNumberValue === 'number' ? formatEditableValue(fallbackNumberValue) : '';
  }, [editingValues, makeCellKey, formatEditableValue]);

  // Função para replicar valores do primeiro mês para os demais
  const handleReplicateFromFirstMonth = useCallback((categoryId) => {
    const firstMonth = 1; // Janeiro é o mês 1
    
    // Determinar quais cenários estão visíveis
    const scenarios = [];
    if (showVP) scenarios.push('vp');
    if (showVM) scenarios.push('vm');
    if (showVO) scenarios.push('vo');

    scenarios.forEach((scenario) => {
      // Obter valor do primeiro mês
      const baseValue = getAggregatedValue(categoryId, firstMonth, scenario);
      
      // Considerar "vazio" apenas null/undefined; 0 é valor válido
      if (baseValue === null || baseValue === undefined) return;

      // Replicar para todos os meses seguintes (2 a 12)
      for (let month = 2; month <= 12; month++) {
        onChangeValue(categoryId, month, scenario, baseValue);
      }
    });
  }, [showVP, showVM, showVO, getAggregatedValue, onChangeValue]);

  // Verificar se há valores no primeiro mês para uma categoria
  const hasValuesInFirstMonth = useCallback((categoryId) => {
    const firstMonth = 1;
    const scenarios = [];
    if (showVP) scenarios.push('vp');
    if (showVM) scenarios.push('vm');
    if (showVO) scenarios.push('vo');

    return scenarios.some((scenario) => {
      const value = getAggregatedValue(categoryId, firstMonth, scenario);
      return value !== null && value !== undefined && value !== 0;
    });
  }, [showVP, showVM, showVO, getAggregatedValue]);

  const columns = useMemo(() => {
    const getValueFor = (record, month, field) => {
      if (!record) return undefined;
      if (record.__virtual) {
        const children = record.children || [];
        let sum = 0;
        let hasAny = false;
        children.forEach((child) => {
          const childVal = getValueFor(child, month, field);
          if (typeof childVal === 'number') {
            sum += childVal;
            hasAny = true;
          }
        });
        return hasAny ? round2(sum) : undefined;
      }
      const raw = getAggregatedValue(record.id, month, field);
      return typeof raw === 'number' ? round2(raw) : raw;
    };

    const isEditableLeaf = (record) => !record?.__virtual && isLeaf(record.id);

    const formatNumber = (val) =>
      typeof val === 'number'
        ? val.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : '-';

    const sorterFactory = (month, field) => (a, b) => {
      const aVal = getValueFor(a, month, field);
      const bVal = getValueFor(b, month, field);
      const norm = (value) =>
        typeof value === 'number' && !Number.isNaN(value)
          ? value
          : Number.NEGATIVE_INFINITY;
      return norm(aVal) - norm(bVal);
    };

    const renderEditableBudgetInput = (record, month, scenario, val) => (
      <Input
        value={getCellInputValue(record.id, month, scenario, val)}
        onChange={(e) =>
          setCellEditingValue(
            record.id,
            month,
            scenario,
            e?.target?.value ?? ''
          )
        }
        onBlur={() => commitCellValue(record.id, month, scenario)}
        onPressEnter={() => commitCellValue(record.id, month, scenario)}
        inputMode="decimal"
        style={{
          width: '100%',
          backgroundColor: theme.inputBackground,
          color: theme.textPrimary,
        }}
      />
    );

    const cols = [
      {
        title: 'Categoria',
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        width: 280,
        sorter: (a, b) =>
          String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR', {
            sensitivity: 'base',
          }),
        render: (text, record) => {
          const isLeafCategory = isEditableLeaf(record);
          const hasValues = isLeafCategory && hasValuesInFirstMonth(record.id);
          
          return (
            <div style={{ 
              display: 'inline-flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              width: 'calc(100% - 24px)',
              gap: 8,
              verticalAlign: 'middle'
            }}>
              <Text 
                style={{ 
                  fontWeight: 500, 
                  flex: 1, 
                  minWidth: 0,
                  margin: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {text}
              </Text>
              {isLeafCategory && (
                <Tooltip title={!hasValues ? "Não há valores no 1º mês para replicar" : "Replicar valores do primeiro mês para os demais meses"}>
                  <Button
                    type="link"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => handleReplicateFromFirstMonth(record.id)}
                    disabled={!hasValues}
                    style={{ 
                      padding: '0 4px',
                      height: 'auto',
                      fontSize: '12px',
                      flexShrink: 0
                    }}
                  >
                    Replicar 1º mês
                  </Button>
                </Tooltip>
              )}
            </div>
          );
        },
      }
    ];

    for (let m = 1; m <= 12; m++) {
      if (hiddenMonths && hiddenMonths.has(m)) {
        continue;
      }
      const monthChildren = [];
      if (showVP) {
        monthChildren.push({
          title: 'VP',
          key: `m${m}_vp`,
          dataIndex: `m${m}_vp`,
          width: 110,
          sorter: sorterFactory(m, 'vp'),
          render: (_, record) => {
            const val = getValueFor(record, m, 'vp');
            if (!isEditableLeaf(record)) {
              return <span style={{ color: theme.textPrimary }}>{formatNumber(val)}</span>;
            }
            return renderEditableBudgetInput(record, m, 'vp', val);
          }
        });
      }
      if (showVM) {
        monthChildren.push({
          title: 'VM',
          key: `m${m}_vm`,
          dataIndex: `m${m}_vm`,
          width: 110,
          sorter: sorterFactory(m, 'vm'),
          render: (_, record) => {
            const val = getValueFor(record, m, 'vm');
            if (!isEditableLeaf(record)) {
              return <span style={{ color: theme.textPrimary }}>{formatNumber(val)}</span>;
            }
            return renderEditableBudgetInput(record, m, 'vm', val);
          }
        });
      }
      if (showVO) {
        monthChildren.push({
          title: 'VO',
          key: `m${m}_vo`,
          dataIndex: `m${m}_vo`,
          width: 110,
          sorter: sorterFactory(m, 'vo'),
          render: (_, record) => {
            const val = getValueFor(record, m, 'vo');
            if (!isEditableLeaf(record)) {
              return <span style={{ color: theme.textPrimary }}>{formatNumber(val)}</span>;
            }
            return renderEditableBudgetInput(record, m, 'vo', val);
          }
        });
      }
      cols.push({
        key: `month_${m}`,
        title: (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              cursor: onToggleMonth ? 'pointer' : 'default',
              borderLeft: `2px solid ${theme.border}`,
              paddingLeft: 8,
              color: theme.textPrimary,
            }}
            onClick={() => onToggleMonth && onToggleMonth(m)}
          >
            {monthLabels[m - 1]}
            {onToggleMonth && <EyeInvisibleOutlined style={{ color: theme.textSecondary }} />}
          </span>
        ),
        children: monthChildren
      });
    }

    return cols;
  }, [showVM, showVP, showVO, getAggregatedValue, isLeaf, hiddenMonths, onToggleMonth, monthLabels, handleReplicateFromFirstMonth, hasValuesInFirstMonth, theme, getCellInputValue, setCellEditingValue, commitCellValue]);

  // Memoizar o componente de célula customizado para evitar re-renderizações
  const customCellComponent = useMemo(() => {
    return (props) => {
      if (props.className?.includes('ant-table-cell-fix-left-last')) {
        return (
          <td {...props} className={`${props.className} budget-table-category-cell`}>
            {props.children}
          </td>
        );
      }
      return <td {...props}>{props.children}</td>;
    };
  }, []);

  // Memoizar os components para evitar re-renderizações
  const tableComponents = useMemo(() => ({
    body: {
      cell: customCellComponent
    }
  }), [customCellComponent]);

  // Memoizar onRow para evitar re-renderizações
  const handleRow = useCallback((record, index) => {
    return {
      style: {
        backgroundColor: index % 2 === 0 ? theme.backgroundSecondary : theme.backgroundTertiary,
        color: theme.textPrimary
      }
    };
  }, [theme]);

  // Memoizar expandable para evitar re-renderizações
  const expandableConfig = useMemo(() => ({
    rowExpandable: (record) => Array.isArray(record.children) && record.children.length > 0
  }), []);

  return (
    <>
      <style>{`
        .budget-table-themed .ant-table {
          background-color: ${theme.cardBackground};
          color: ${theme.textPrimary};
        }
        .budget-table-themed .ant-table-container {
          border: 1px solid ${theme.border};
          border-radius: 8px;
          background-color: ${theme.cardBackground};
        }
        .budget-table-themed .ant-table-thead > tr > th {
          background-color: ${theme.backgroundSecondary} !important;
          color: ${theme.textPrimary} !important;
          border-bottom: 1px solid ${theme.border} !important;
        }
        .budget-table-themed .ant-table-tbody > tr > td {
          border-bottom: 1px solid ${theme.border} !important;
          color: ${theme.textPrimary};
        }
        .budget-table-themed .ant-table-tbody > tr.ant-table-row:hover > td {
          background-color: ${theme.backgroundTertiary} !important;
        }
        .budget-table-themed .ant-table-cell-fix-left,
        .budget-table-themed .ant-table-cell-fix-left-last {
          background-color: ${theme.backgroundSecondary} !important;
        }
        .budget-table-themed .ant-table-row:hover .ant-table-cell-fix-left,
        .budget-table-themed .ant-table-row:hover .ant-table-cell-fix-left-last {
          background-color: ${theme.backgroundTertiary} !important;
        }
        .budget-table-themed .ant-input-number {
          background-color: ${theme.inputBackground};
          border-color: ${theme.border};
        }
        .budget-table-themed .ant-input-number input {
          color: ${theme.textPrimary};
        }
        .budget-table-category-cell {
          display: flex !important;
          align-items: center !important;
          white-space: nowrap !important;
        }
        .budget-table-category-cell .ant-table-row-expand-icon {
          flex-shrink: 0;
          margin-right: 8px;
        }
        .budget-table-category-cell > div {
          display: inline-flex !important;
          align-items: center;
          width: 100%;
        }
      `}</style>
      <Table
        className="budget-table-themed"
        columns={columns}
        dataSource={filteredDataSource}
        rowKey="id"
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
        expandable={expandableConfig}
        onRow={handleRow}
        components={tableComponents}
      />
    </>
  );
};

export default BudgetTable;
